import type { ReactNode } from "react";

/**
 * O CONTRATO DE LAYOUT das telas do painel que são documento (Agenda,
 * Pacientes, Resultados, Sistema). Ajustes tem moldura própria (Ajustes.tsx),
 * e Conversas e a Bancada são bancada de trabalho, coladas nas bordas.
 *
 * Antes cada tela inventava a própria largura — 1000px centrado aqui,
 * `max-w-4xl` ali, largura cheia acolá, e duas sangrando com cabeçalho
 * próprio. O efeito de andar entre elas era de páginas soltas, não de um app.
 *
 * A regra:
 *   <Tela>  → rolagem, fundo e respiro de 32px
 *     <CabecalhoPagina titulo descricao acao />
 *     …conteúdo…
 *
 * A coluna tem 1200px e é centrada. Numa tela larga sobra margem dos dois
 * lados, e isso é escolha: linha de texto e tabela ficam legíveis, e a pessoa
 * reconhece que é o mesmo app em qualquer aba.
 *
 * No celular o respiro cai para 16px e as ações do cabeçalho descem de linha.
 */
export function Tela({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={`anim-fade h-full overflow-y-auto bg-[var(--color-fundo)] px-4 py-5 text-[var(--color-tinta)] md:px-8 md:py-7 ${className ?? ""}`}>
      <div className="mx-auto w-full max-w-[1200px] pb-12">{children}</div>
    </div>
  );
}

/** O cabeçalho de toda tela: um só tamanho de título em todas. As ações quebram para baixo no celular. */
export function CabecalhoPagina({
  titulo,
  descricao,
  acao,
}: {
  titulo: string;
  descricao?: ReactNode;
  acao?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div className="min-w-0">
        <h1 className="text-[22px] font-bold tracking-tight text-[var(--color-tinta)] md:text-[26px]">{titulo}</h1>
        {descricao ? (
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[var(--color-suave)]">{descricao}</p>
        ) : null}
      </div>
      {acao ? <div className="flex shrink-0 flex-wrap items-center gap-2">{acao}</div> : null}
    </header>
  );
}
